import { useFormField } from "@/components/elements/FormField";
import { FC, useEffect, useRef, useState } from "react";
import { Editor as DEditor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

import formatBoldIMG from "@/assets/images/format_bold.png";
import formatClearIMG from "@/assets/images/format_clear.png";
import formatListBullIMG from "@/assets/images/format_list_bulleted.png";
import formatListNumIMG from "@/assets/images/format_list_numbered.png";
import formatQuoteIMG from "@/assets/images/format_quote.png";
import formatStrikeIMG from "@/assets/images/strikethrough_s.png";
import { hexToRgba } from "@/utils/pieces";
import { isUrl } from "@/utils/url";
import { background, divider, state } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import styled from "@emotion/styled";
import { Box, css } from "@mui/material";
import { EditorState, Modifier, SelectionState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import { createContentStateFromHtml, normalizeHtmlFromEditor } from "@/components/elements/Editor/editorHtmlUtils";

const applyUrlEntities = (state: EditorState): EditorState => {
  const urlRegex = /https?:\/\/[^\s<>"']+(?<![.,!?])/g;
  const contentState = state.getCurrentContent();
  let newContentState = contentState;
  let changed = false;

  contentState.getBlocksAsArray().forEach((block) => {
    const text = block.getText();
    const blockKey = block.getKey();

    // Collect URL ranges and apply LINK entities
    const urlPositions = new Set<number>();
    let match;
    while ((match = urlRegex.exec(text)) !== null) {
      const start = match.index;
      const end = start + match[0].length;
      for (let i = start; i < end; i++) urlPositions.add(i);

      newContentState = newContentState.createEntity("LINK", "MUTABLE", { url: match[0] });
      const entityKey = newContentState.getLastCreatedEntityKey();
      newContentState = Modifier.applyEntity(
        newContentState,
        SelectionState.createEmpty(blockKey).merge({ anchorOffset: start, focusOffset: end, isBackward: false }) as SelectionState,
        entityKey,
      );
      changed = true;
    }

    // Strip LINK entity from non-URL characters that inherited it (e.g. chars typed right after a URL)
    let stripStart = -1;
    for (let i = 0; i <= text.length; i++) {
      const shouldStrip =
        i < text.length &&
        !urlPositions.has(i) &&
        (() => {
          const key = block.getEntityAt(i);
          if (!key) return false;
          try {
            return newContentState.getEntity(key).getType() === "LINK";
          } catch {
            return false;
          }
        })();

      if (shouldStrip && stripStart === -1) {
        stripStart = i;
      } else if (!shouldStrip && stripStart !== -1) {
        newContentState = Modifier.applyEntity(
          newContentState,
          SelectionState.createEmpty(blockKey).merge({ anchorOffset: stripStart, focusOffset: i, isBackward: false }) as SelectionState,
          null,
        );
        changed = true;
        stripStart = -1;
      }
    }
  });

  if (!changed) return state;
  return EditorState.forceSelection(EditorState.set(state, { currentContent: newContentState }), state.getSelection());
};

export type TEditorProps = {
  value?: string;
  placeholder?: string;
  nonControl?: boolean;
  onChange?: (v: string) => void;
};

export const Editor: FC<TEditorProps> = ({ nonControl = false, placeholder = "入力してください", onChange, value }) => {
  const isInternalUpdateRef = useRef(false);
  const isComposingRef = useRef(false);
  const latestEditorStateRef = useRef<EditorState | null>(null);
  const lastAppliedContentRef = useRef<ReturnType<EditorState["getCurrentContent"]> | null>(null);
  const composingStartSelectionRef = useRef<SelectionState | null>(null);
  const composingInlineStyleRef = useRef<ReturnType<EditorState["getCurrentInlineStyle"]> | null>(null);

  const createEditorStateFromHtml = (html: string) => EditorState.createWithContent(createContentStateFromHtml(html));

  const getHtmlFromEditorState = (state: EditorState) => normalizeHtmlFromEditor(draftToHtml(convertToRaw(state.getCurrentContent())));

  const { field } = useFormField(nonControl);
  const [editorState, setEditorState] = useState(() => applyUrlEntities(createEditorStateFromHtml(field?.value ?? value ?? "")));

  useEffect(() => {
    latestEditorStateRef.current = editorState;
  }, [editorState]);

  useEffect(() => {
    if (isComposingRef.current) return;

    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    const externalValue = field?.value ?? value ?? "";
    if (!externalValue || getHtmlFromEditorState(editorState) === externalValue) return;

    setEditorState(applyUrlEntities(createEditorStateFromHtml(externalValue)));
  }, [value, field?.value]);

  const onEditorStateChange = (incomingEditorState: EditorState) => {
    let newEditorState = incomingEditorState;
    const wasComposing = isComposingRef.current;
    const isComposing = newEditorState.isInCompositionMode();

    if (!wasComposing && isComposing) {
      composingStartSelectionRef.current = newEditorState.getSelection();
      const inlineStyle = (latestEditorStateRef.current ?? editorState).getCurrentInlineStyle();
      composingInlineStyleRef.current = inlineStyle && inlineStyle.size > 0 ? inlineStyle : null;
    }

    if (wasComposing && !isComposing) {
      newEditorState = applyStyleToComposedRange(newEditorState);
      composingStartSelectionRef.current = null;
      composingInlineStyleRef.current = null;
    }

    isComposingRef.current = isComposing;

    if (!isComposing) {
      const currentContent = newEditorState.getCurrentContent();
      if (currentContent !== lastAppliedContentRef.current) {
        newEditorState = applyUrlEntities(newEditorState);
        lastAppliedContentRef.current = newEditorState.getCurrentContent();
      }
    }

    setEditorState(newEditorState);
    latestEditorStateRef.current = newEditorState;

    if (isComposing) return;

    updateValue(newEditorState);
  };

  const applyStyleToComposedRange = (state: EditorState): EditorState => {
    const startSelection = composingStartSelectionRef.current;
    const inlineStyle = composingInlineStyleRef.current;
    if (!startSelection || !inlineStyle || inlineStyle.size === 0) return state;

    const blockKey = startSelection.getStartKey();
    const startOffset = startSelection.getStartOffset();
    const currentSelection = state.getSelection();

    if (currentSelection.getEndKey() !== blockKey) return state;

    const endOffset = currentSelection.getEndOffset();
    if (endOffset <= startOffset) return state;

    const range = SelectionState.createEmpty(blockKey).merge({
      anchorKey: blockKey,
      anchorOffset: startOffset,
      focusKey: blockKey,
      focusOffset: endOffset,
      isBackward: false,
    });

    let content = state.getCurrentContent();
    inlineStyle.forEach((style) => {
      if (style) content = Modifier.applyInlineStyle(content, range, style);
    });

    const pushedState = EditorState.push(state, content, "change-inline-style");
    return EditorState.forceSelection(pushedState, currentSelection);
  };

  const handleClear = () => {
    const currentContent = editorState.getCurrentContent();
    const newEditorState = EditorState.createEmpty();

    const selection = newEditorState.getSelection().merge({
      anchorOffset: currentContent.getPlainText("").length,
      focusOffset: currentContent.getPlainText("").length,
    });

    setEditorState(EditorState.forceSelection(newEditorState, selection));
  };

  const updateValue = (editorState: EditorState) => {
    let htmlContent = getHtmlFromEditorState(editorState);

    if (isContentEmpty(htmlContent)) {
      htmlContent = "";
    }
    isInternalUpdateRef.current = true;
    onChange?.(htmlContent);
    field?.onChange(htmlContent);
  };

  const isContentEmpty = (htmlContent: string) => {
    const elm = document.createElement("div");
    elm.innerHTML = htmlContent;
    const textContent = elm.textContent?.replace(/[\s\u00A0\u200B\uFEFF]/g, "");
    elm.remove();
    return !textContent;
  };

  const handleBlur = () => {
    if (!isComposingRef.current) updateValue(editorState);
    field?.onBlur();
  };

  const handlePastedText = (text: string, html: string, state: EditorState, onChange: (v: EditorState) => void) => {
    if (!text) return true;

    const currentContent = state.getCurrentContent();
    const selection = editorState.getSelection();

    // If there's a selection, replace the selected text, otherwise insert the pasted text at the current cursor position.
    if (!selection.isCollapsed()) {
      // Replace the selected text with the pasted text
      const contentState = Modifier.replaceText(currentContent, selection, text);
      onChange(EditorState.push(editorState, contentState, "insert-characters"));
    } else {
      // If no text is selected (i.e., the cursor is at a point), insert text as normal
      currentContent.createEntity("LINK", "MUTABLE", { url: text });
      const entityKey = currentContent.getLastCreatedEntityKey();
      const contentState = Modifier.insertText(currentContent, selection, text, undefined, isUrl(text) ? entityKey : undefined);
      onChange(EditorState.push(editorState, contentState, "insert-characters"));
    }

    return true;
  };

  return (
    <EditorWrap>
      <DEditor
        editorState={editorState}
        onEditorStateChange={onEditorStateChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        toolbarCustomButtons={[<ClearEditor onClick={handleClear} />]}
        handlePastedText={handlePastedText}
        toolbar={{
          options: ["inline", "blockType", "list"],
          inline: {
            options: ["bold", "strikethrough"],
            bold: { icon: formatBoldIMG },
            strikethrough: { icon: formatStrikeIMG },
          },
          blockType: {
            inDropdown: false,
            options: ["Blockquote"],
            className: "block-type",
          },
          list: {
            options: ["unordered", "ordered"],
            unordered: { icon: formatListBullIMG },
            ordered: { icon: formatListNumIMG },
          },
        }}
      />
    </EditorWrap>
  );
};

type TClearEditorProps = {
  onClick: () => void;
};

const ClearEditor: FC<TClearEditorProps> = ({ onClick }) => {
  return (
    <Box className="rdw-option-wrapper" onClick={onClick}>
      <img src={formatClearIMG} alt="clear" />
    </Box>
  );
};

const EditorWrap = styled(Box)`
  .rdw-editor-toolbar {
    background-color: ${background.disable};
    border: 1px solid ${divider.middle};
    border-radius: 0;
    border-top-left-radius: ${rounded.sm};
    border-top-right-radius: ${rounded.sm};
    margin-bottom: 0;
  }
  .rdw-editor-main {
    background-color: ${background.white};
    border: 1px solid ${divider.middle};
    border-bottom-left-radius: ${rounded.sm};
    border-bottom-right-radius: ${rounded.sm};
    padding: 8px;
    min-height: 327px;
    ${css(typography.body14)}
    .public-DraftStyleDefault-block {
      margin: 0;
    }
    .rdw-link-decorator-wrapper {
      display: inline-block;
      a {
        color: ${state.link_1};
        text-decoration: underline;
        pointer-events: none;
        & + img {
          display: none;
        }
      }
    }
  }
  .rdw-option-wrapper {
    width: 24px;
    height: 24px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    border: none;
    box-shadow: none !important;
    color: transparent;
    background-color: transparent;
    &[aria-selected="true"] {
      background-color: ${hexToRgba(background.dark, 0.2)};
    }
  }
  .block-type {
    .rdw-option-wrapper {
      &:nth-of-type(1) {
        position: relative;

        &::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: url(${formatQuoteIMG}) no-repeat;
          z-index: 0;
        }
      }
    }
  }
`;
